import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useState } from 'react';
import Icon from '../../../components/AppIcon';

const TaskCard = ({ 
  task, 
  quadrantColor, 
  onEdit, 
  onDelete, 
  onToggleComplete,
  onSwipeAction 
}) => {
  const [touchStart, setTouchStart] = useState(null);
  const [touchOffset, setTouchOffset] = useState(0);
  const [showActions, setShowActions] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task?.id });

  const style = {
    transform: CSS?.Transform?.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleTouchStart = (e) => {
    setTouchStart(e?.touches?.[0]?.clientX);
  };

  const handleTouchMove = (e) => {
    if (!touchStart) return;
    const currentTouch = e?.touches?.[0]?.clientX;
    const diff = currentTouch - touchStart;
    
    if (Math.abs(diff) > 50) {
      setTouchOffset(diff);
      setShowActions(true);
    }
  };

  const handleTouchEnd = () => {
    if (Math.abs(touchOffset) > 100) {
      if (touchOffset > 0) {
        onSwipeAction(task?.id, 'complete');
      } else {
        onSwipeAction(task?.id, 'delete');
      }
    }
    setTouchStart(null);
    setTouchOffset(0);
    setShowActions(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow?.setDate(tomorrow?.getDate() + 1);

    if (date?.toDateString() === today?.toDateString()) {
      return 'Today';
    } else if (date?.toDateString() === tomorrow?.toDateString()) {
      return 'Tomorrow';
    } else {
      return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const isOverdue = new Date(task.dueDate) < new Date() && !task?.completed;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      className={`relative bg-background rounded-lg border border-border p-3 md:p-4 cursor-move touch-none ${
        task?.completed ? 'opacity-60' : ''
      } ${isDragging ? 'shadow-soft-xl' : 'shadow-soft hover:shadow-soft-md'} transition-all duration-250`}
    >
      {/* Swipe Actions Background */}
      {showActions && (
        <div className="absolute inset-0 flex items-center justify-between px-4 rounded-lg">
          {touchOffset > 0 && (
            <div className="flex items-center gap-2 text-success">
              <Icon name="Check" size={20} color="var(--color-success)" />
              <span className="text-sm font-medium">Complete</span>
            </div>
          )}
          {touchOffset < 0 && (
            <div className="flex items-center gap-2 text-destructive ml-auto">
              <span className="text-sm font-medium">Delete</span>
              <Icon name="Trash2" size={20} color="var(--color-destructive)" />
            </div>
          )}
        </div>
      )}
      {/* Task Content */}
      <div className="flex items-start gap-3">
        {/* Checkbox */}
        <button
          onClick={(e) => {
            e?.stopPropagation();
            onToggleComplete(task?.id);
          }}
          className="flex-shrink-0 w-5 h-5 md:w-6 md:h-6 rounded border-2 border-border hover:border-primary transition-colors flex items-center justify-center mt-0.5"
          style={{
            backgroundColor: task?.completed ? quadrantColor : 'transparent',
            borderColor: task?.completed ? quadrantColor : undefined,
          }}
        >
          {task?.completed && (
            <Icon name="Check" size={14} color="white" strokeWidth={3} />
          )}
        </button>

        {/* Task Details */}
        <div className="flex-1 min-w-0">
          <h4 className={`text-sm md:text-base font-medium text-foreground mb-1 ${
            task?.completed ? 'line-through' : ''
          }`}>
            {task?.title}
          </h4>

          {/* Due Date & Time */}
          <div className="flex items-center gap-2 mb-2">
            <div className={`flex items-center gap-1 text-xs md:text-sm ${
              isOverdue ? 'text-destructive' : 'text-muted-foreground'
            }`}>
              <Icon 
                name={isOverdue ? "AlertCircle" : "Calendar"} 
                size={14} 
                color="currentColor" 
              />
              <span>{formatDate(task?.dueDate)}</span>
            </div>
            {task?.estimatedTime && (
              <div className="flex items-center gap-1 text-xs md:text-sm text-muted-foreground">
                <Icon name="Clock" size={14} color="currentColor" />
                <span>{task?.estimatedTime}</span>
              </div>
            )}
          </div>

          {/* Tags */}
          {task?.tags && task?.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {task?.tags?.map((tag, index) => (
                <span
                  key={index}
                  className="px-2 py-0.5 rounded text-xs font-medium"
                  style={{
                    backgroundColor: quadrantColor + '20',
                    color: quadrantColor,
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Recurrence Badge */}
          {task?.recurrence && task?.recurrence !== 'none' && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Icon name="Repeat" size={12} color="currentColor" />
              <span className="capitalize">{task?.recurrence}</span>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={(e) => {
              e?.stopPropagation();
              onEdit(task);
            }}
            className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
            aria-label="Edit task"
          >
            <Icon name="Edit2" size={16} color="var(--color-muted-foreground)" />
          </button>
          <button
            onClick={(e) => {
              e?.stopPropagation();
              onDelete(task?.id);
            }}
            className="w-8 h-8 rounded-lg hover:bg-destructive/10 flex items-center justify-center transition-colors"
            aria-label="Delete task"
          >
            <Icon name="Trash2" size={16} color="var(--color-destructive)" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TaskCard;